/**
 * Vue Composable for Printer Utility
 * Provides reactive state management and easy integration with Vue components
 */

import { ref, reactive, computed, onUnmounted } from 'vue'
import PrinterUtility from '@/utils/printerUtility'
import { useToast } from 'vue-toastification'

/**
 * Shared printer state across all components
 */
const globalPrinterState = reactive({
  isConnected: false,
  isConnecting: false,
  isScanning: false,
  isPrinting: false,
  connectedDevice: null,
  availableDevices: [],
  lastError: null,
  config: {
    paperWidth: 58,
    autoReconnect: true,
    cutPaper: true,
    includeGST: true,
    includeBranding: true,
    debug: false
  }
})

let printerUtility = null

/**
 * Initialize printer utility with current config
 */
function initializePrinterUtility() {
  if (!printerUtility) {
    printerUtility = new PrinterUtility(globalPrinterState.config)
    setupEventListeners()
  }
  return printerUtility
}

/**
 * Setup event listeners for printer utility
 */
function setupEventListeners() {
  if (!printerUtility) return

  // Scanning events
  printerUtility.addEventListener('scanning', (event) => {
    globalPrinterState.isScanning = event.detail
  })

  printerUtility.addEventListener('devicesFound', (event) => {
    globalPrinterState.availableDevices = event.detail
  })

  // Connection events
  printerUtility.addEventListener('connecting', (event) => {
    globalPrinterState.isConnecting = true
    globalPrinterState.lastError = null
  })

  printerUtility.addEventListener('connected', (event) => {
    globalPrinterState.isConnected = true
    globalPrinterState.isConnecting = false
    globalPrinterState.connectedDevice = event.detail
    globalPrinterState.lastError = null
  })

  printerUtility.addEventListener('disconnected', (event) => {
    globalPrinterState.isConnected = false
    globalPrinterState.isConnecting = false
    globalPrinterState.connectedDevice = null
  })

  printerUtility.addEventListener('connectionError', (event) => {
    globalPrinterState.isConnected = false
    globalPrinterState.isConnecting = false
    globalPrinterState.lastError = event.detail
  })

  // Printing events
  printerUtility.addEventListener('printing', (event) => {
    globalPrinterState.isPrinting = true
    globalPrinterState.lastError = null
  })

  printerUtility.addEventListener('printed', (event) => {
    globalPrinterState.isPrinting = false
  })

  printerUtility.addEventListener('printError', (event) => {
    globalPrinterState.isPrinting = false
    globalPrinterState.lastError = event.detail
  })
}

/**
 * Main composable function
 */
export function usePrinter() {
  const toast = useToast()

  // Initialize printer utility
  const printer = initializePrinterUtility()

  // Reactive state
  const isSupported = computed(() => printer.isSupported())
  const status = computed(() => printer.getStatus())
  
  const connectionStatus = computed(() => {
    if (globalPrinterState.isConnecting) return 'connecting'
    if (globalPrinterState.isConnected) return 'connected'
    return 'disconnected'
  })

  /**
   * Discover available printers
   */
  const discoverPrinters = async () => {
    try {
      globalPrinterState.lastError = null
      const devices = await printer.discoverPrinters()
      
      if (devices.length === 0) {
        toast.info('No printers found. Make sure your printer is in pairing mode.')
      }
      
      return devices
    } catch (error) {
      console.error('Printer discovery failed:', error)
      toast.error(`Failed to discover printers: ${error.message}`)
      throw error
    }
  }

  /**
   * Connect to a specific printer
   */
  const connectToPrinter = async (device) => {
    try {
      globalPrinterState.lastError = null
      await printer.connect(device)
      toast.success(`Connected to ${device.name}`)
      return true
    } catch (error) {
      console.error('Printer connection failed:', error)
      toast.error(`Failed to connect to ${device.name}: ${error.message}`)
      throw error
    }
  }

  /**
   * Disconnect from current printer
   */
  const disconnectPrinter = async () => {
    try {
      await printer.disconnect()
      toast.success('Printer disconnected')
      return true
    } catch (error) {
      console.error('Printer disconnection failed:', error)
      toast.error(`Failed to disconnect printer: ${error.message}`)
      throw error
    }
  }

  /**
   * Print a receipt
   */
  const printReceipt = async (transaction, business = {}) => {
    try {
      if (!globalPrinterState.isConnected) {
        throw new Error('No printer connected')
      }

      globalPrinterState.lastError = null
      await printer.printReceipt(transaction, business)
      toast.success('Receipt printed successfully')
      return true

    } catch (error) {
      console.error('Print failed:', error)
      
      // Provide helpful error messages
      if (error.message.includes('No printer connected')) {
        toast.error('Please connect to a printer first')
      } else if (error.message.includes('Failed to send data')) {
        toast.error('Printer communication failed. Check connection.')
      } else {
        toast.error(`Print failed: ${error.message}`)
      }
      
      throw error
    }
  }

  /**
   * Print receipt using template (NEW)
   */
  const printReceiptWithTemplate = async (templateId, transaction, business = {}, options = {}) => {
    try {
      if (!globalPrinterState.isConnected) {
        throw new Error('No printer connected')
      }

      globalPrinterState.lastError = null
      await printer.printReceiptWithTemplate(templateId, transaction, business, options)
      toast.success(`Receipt printed with ${templateId} template`)
      return true

    } catch (error) {
      console.error('Template print failed:', error)
      
      // Provide helpful error messages
      if (error.message.includes('No printer connected')) {
        toast.error('Please connect to a printer first')
      } else if (error.message.includes('not found')) {
        toast.error(`Template '${templateId}' not available`)
      } else if (error.message.includes('not compatible')) {
        toast.error(`Template not compatible with current paper width`)
      } else {
        toast.error(`Print failed: ${error.message}`)
      }
      
      throw error
    }
  }

  /**
   * Get available templates for current paper width
   */
  const getAvailableTemplates = async () => {
    try {
      return await printer.getAvailableTemplates()
    } catch (error) {
      console.error('Failed to get templates:', error)
      return []
    }
  }

  /**
   * Get recommended template for current configuration
   */
  const getRecommendedTemplate = async (category = 'detailed') => {
    try {
      return await printer.getRecommendedTemplate(category)
    } catch (error) {
      console.error('Failed to get recommended template:', error)
      return 'detailed-58' // fallback
    }
  }

  /**
   * Test printer with a simple print
   */
  const testPrint = async () => {
    try {
      if (!globalPrinterState.isConnected) {
        throw new Error('No printer connected')
      }

      await printer.testPrint()
      toast.success('Test print completed')
      return true

    } catch (error) {
      console.error('Test print failed:', error)
      toast.error(`Test print failed: ${error.message}`)
      throw error
    }
  }

  /**
   * Update printer configuration
   */
  const updateConfig = (newConfig) => {
    globalPrinterState.config = { ...globalPrinterState.config, ...newConfig }
    printer.updateConfig(globalPrinterState.config)
    
    // Save to localStorage
    localStorage.setItem('printerConfig', JSON.stringify(globalPrinterState.config))
  }

  /**
   * Load configuration from localStorage
   */
  const loadConfig = () => {
    try {
      const savedConfig = localStorage.getItem('printerConfig')
      if (savedConfig) {
        const config = JSON.parse(savedConfig)
        updateConfig(config)
      }
    } catch (error) {
      console.warn('Failed to load printer config from localStorage:', error)
    }
  }

  /**
   * Auto-connect to last connected printer
   */
  const autoConnect = async () => {
    try {
      const lastPrinter = localStorage.getItem('lastConnectedPrinter')
      if (!lastPrinter) return false

      const printerInfo = JSON.parse(lastPrinter)
      
      // Try to get the device from browser's Bluetooth cache
      const devices = await navigator.bluetooth.getDevices()
      const device = devices.find(d => d.id === printerInfo.id)
      
      if (device) {
        await connectToPrinter(device)
        return true
      }
      
      return false

    } catch (error) {
      console.warn('Auto-connect failed:', error)
      return false
    }
  }

  /**
   * Clear all stored printer data
   */
  const clearStoredData = () => {
    localStorage.removeItem('lastConnectedPrinter')
    localStorage.removeItem('printerConfig')
    globalPrinterState.availableDevices = []
    globalPrinterState.connectedDevice = null
    globalPrinterState.lastError = null
  }

  // Legacy compatibility methods for existing components
  const scanForPrinters = discoverPrinters
  const bluetoothStatus = connectionStatus
  const connectedPrinter = computed(() => globalPrinterState.connectedDevice)
  const availablePrinters = computed(() => globalPrinterState.availableDevices)
  const isScanning = computed(() => globalPrinterState.isScanning)
  const showPrinterList = computed(() => globalPrinterState.availableDevices.length > 0)
  
  // Legacy settings structure for compatibility
  const globalPrintSettings = reactive({
    printMethod: 'thermal',
    autoPrint: false,
    printerSettings: {
      paperWidth: globalPrinterState.config.paperWidth.toString(),
      cutPaper: globalPrinterState.config.cutPaper,
      beepOnPrint: false,
      printCopies: 1
    }
  })

  const shouldShowPrinterSection = computed(() => true)
  const updatePrintMethod = (method) => { globalPrintSettings.printMethod = method }
  const updateAutoPrint = (enabled) => { globalPrintSettings.autoPrint = enabled }

  // Load config on initialization
  loadConfig()

  // Cleanup on unmount
  onUnmounted(() => {
    // Don't disconnect here as other components might be using the printer
    // The printer connection should persist across component lifecycle
  })

  return {
    // New API
    isSupported,
    isConnected: computed(() => globalPrinterState.isConnected),
    isConnecting: computed(() => globalPrinterState.isConnecting),
    isScanning,
    isPrinting: computed(() => globalPrinterState.isPrinting),
    connectionStatus,
    connectedDevice: computed(() => globalPrinterState.connectedDevice),
    availableDevices: computed(() => globalPrinterState.availableDevices),
    lastError: computed(() => globalPrinterState.lastError),
    config: computed(() => globalPrinterState.config),
    status,

    // Methods
    discoverPrinters,
    connectToPrinter,
    disconnectPrinter,
    printReceipt,
    printReceiptWithTemplate,
    getAvailableTemplates,
    getRecommendedTemplate,
    testPrint,
    updateConfig,
    loadConfig,
    autoConnect,
    clearStoredData,

    // Legacy compatibility
    scanForPrinters,
    bluetoothStatus,
    connectedPrinter,
    availablePrinters,
    showPrinterList,
    globalPrintSettings,
    shouldShowPrinterSection,
    updatePrintMethod,
    updateAutoPrint,

    // Direct access to printer utility (for advanced usage)
    printer
  }
}

/**
 * Simple function to check if printing is available
 */
export function isPrintingAvailable() {
  return 'bluetooth' in navigator && 'requestDevice' in navigator.bluetooth
}

/**
 * Get printer status without full composable setup
 */
export function getPrinterStatus() {
  return {
    isConnected: globalPrinterState.isConnected,
    isConnecting: globalPrinterState.isConnecting,
    device: globalPrinterState.connectedDevice
  }
}

/**
 * Get the printer utility instance for low-level access
 * Used by components that need direct ESC/POS control
 */
export function getPrinterUtility() {
  return initializePrinterUtility()
}
